import moment from "moment-timezone";

export const getSharedDateRowSpan = (dateTimesheetMap, timeSheet) => {
	if (isAddedTimeSheet(timeSheet)) {
		return 1;
	}
	const date = timeSheet.date;

	if (!(date in dateTimesheetMap)) {
		return 1;
	}

	const list = dateTimesheetMap[date].filter((item) => {
		if (isNonProductiveTimeSheet(timeSheet)) {
			return isNonProductiveTimeSheet(item)
		}
		if (!isNonProductiveTimeSheet(timeSheet)) {
			return !isNonProductiveTimeSheet(item)
		}
	});

	const index = list.findIndex(({ id }) => id === timeSheet.id);
	if (index === 0) {
		return list.length;
	} else {
		return 0;
	}
};

export const isAddedTimeSheet = (timeSheet) => {
	return !!timeSheet?.isAdded;
};

export const isNonProductiveTimeSheet = (timeSheet) => {
	return !!timeSheet?.isNonProductiveTime;
}

export const getTimeSheetTimeZone = (timeSheet) => {
	if (isAddedTimeSheet(timeSheet)) {
		return timeSheet.location?.timezoneValue;
	}
	return timeSheet.location?.timezoneValue || timeSheet?.timezone;
};

export const isTimeInputDisabled = (timeSheet) => {
	return (
		timeSheet.payCode?.hourType === "amount" ||
		timeSheet.payCode?.hourType === "unit" ||
		(!isAddedTimeSheet(timeSheet) && timeSheet.locked)
	);
};

export const isInputDisabled = (timeSheet) => {
	return !isAddedTimeSheet(timeSheet) && timeSheet.locked;
};

/**
 * Which side of a miss-punch row is the unknown one, and only while it is *still* unknown. Rows
 * flagged before the short-gap rule existed carry no `missPunchSide` and are always a missing
 * clock-out, so they keep reading as "end".
 *
 * A missing start is stored as a placeholder equal to the end, so the row claims no duration. Once
 * someone supplies a real start the two differ and the row reads as resolved straight away, without
 * waiting for the server to clear the flag and the grid to refetch.
 */
export const getUnresolvedMissPunchSide = (timeSheet) => {
	if (!timeSheet?.misspunch) {
		return undefined;
	}
	if ((timeSheet.missPunchSide || "end") === "end") {
		return timeSheet.endDate ? undefined : "end";
	}
	return timeSheet.startDate && timeSheet.startDate === timeSheet.endDate ? "start" : undefined;
};

/**
 * The other half of a continuous miss-punch pair. The short-gap rule voids one punch, which leaves
 * the segment before it without an end and the segment after it without a start - two rows, one
 * unknown moment. Filling either should fill the other, so this finds the immediate neighbour when
 * it is the opposite, still-unresolved side. Neighbours that are not adjacent are not a pair.
 */
export const findContinuousMissPunchSibling = (timeSheet, data) => {
	const side = getUnresolvedMissPunchSide(timeSheet);
	if (!side) {
		return undefined;
	}
	const rows = (data || [])
		.filter((r) => r
			&& r.date === timeSheet.date
			&& !isNonProductiveTimeSheet(r)
			&& !["unit", "amount"].includes(r.payCode?.hourType))
		.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
	const index = rows.findIndex((r) => r.id === timeSheet.id);
	if (index === -1) {
		return undefined;
	}
	const neighbour = side === "start" ? rows[index - 1] : rows[index + 1];
	const neighbourSide = getUnresolvedMissPunchSide(neighbour);
	if (!neighbour || !neighbourSide || neighbourSide === side) {
		return undefined;
	}
	return neighbour;
};

/**
 * The full set of fields to write onto the other half of a continuous miss-punch pair.
 *
 * Writing only the date leaves the row half-updated — its duration, and everything derived from it,
 * keep the values they had while the time was still unknown — so the pinned moment and the duration
 * it implies are always written together, exactly as an edit to that cell would.
 */
export const buildMissPunchPairUpdate = (pair, pinned) => {
	if (!pair || !pinned) {
		return undefined;
	}
	// the neighbour before this row ends where it starts; the one after starts where it ends
	const field = getUnresolvedMissPunchSide(pair) === "end" ? "endDate" : "startDate";
	const startDate = field === "startDate" ? pinned : pair.startDate;
	const endDate = field === "endDate" ? pinned : pair.endDate;
	const duration = startDate && endDate
		? moment.parseZone(endDate).diff(moment.parseZone(startDate), "seconds")
		: 0;
	return { [field]: pinned, duration: duration > 0 ? duration : 0 };
};
